import type { SVGProps } from 'react';

import { cn } from '../lib/cn';
import {
    BRAND_NAME,
    BRAND_PATHS,
    BRAND_SLOGAN,
    BRAND_VIEWBOX,
    WORDMARK_OFFSET_WITH_SLOGAN,
    brandWidth,
} from './paths';

/**
 * The surface the logo sits on. It decides the colour of "Smart" and of the
 * slogan; the beeldmerk and "keur" stay brand green on light and on dark.
 *
 * - `light`: work area, cards, mail on white — "Smart" in ink.
 * - `dark`: the rail, dark headers, the login panel — "Smart" in white.
 * - `green`: a brand-green tile — the whole logo in white.
 */
export type BrandSurface = 'light' | 'dark' | 'green';

interface SurfaceFills {
    mark: string;
    smart: string;
    keur: string;
    slogan: string;
}

const surfaceFills: Record<BrandSurface, SurfaceFills> = {
    light: {
        mark: 'fill-[var(--brand-green)]',
        smart: 'fill-[var(--brand-ink)]',
        keur: 'fill-[var(--brand-green)]',
        slogan: 'fill-[var(--brand-slogan)]',
    },
    dark: {
        mark: 'fill-[var(--brand-green)]',
        smart: 'fill-[var(--brand-ink-on-dark)]',
        keur: 'fill-[var(--brand-green)]',
        slogan: 'fill-[var(--brand-slogan-on-dark)]',
    },
    green: {
        mark: 'fill-[var(--brand-on-green)]',
        smart: 'fill-[var(--brand-on-green)]',
        keur: 'fill-[var(--brand-on-green)]',
        slogan: 'fill-[var(--brand-on-green)]',
    },
};

export type BrandLogoProps = Omit<SVGProps<SVGSVGElement>, 'children' | 'fill' | 'viewBox'> & {
    /** Height in px of the whole logo. The width follows from its proportions. */
    height?: number;
    surface?: BrandSurface;
    /** The composition with "BOUWKUNDIGE KEURINGEN. SLIM GEREGELD." underneath. */
    slogan?: boolean;
    /**
     * Accessible name; defaults to the name (and slogan). `null` makes the logo
     * decorative, for when the same words are already written next to it.
     */
    title?: string | null;
};

/** Beeldmerk plus wordmark, with or without the slogan. */
export function BrandLogo({
    height = 24,
    surface = 'light',
    slogan = false,
    title,
    className,
    ...rest
}: BrandLogoProps) {
    const box = slogan ? BRAND_VIEWBOX.logoWithSlogan : BRAND_VIEWBOX.logo;
    const fills = surfaceFills[surface];
    const name =
        title === undefined ? (slogan ? `${BRAND_NAME}, ${BRAND_SLOGAN}` : BRAND_NAME) : title;
    const offset = slogan ? WORDMARK_OFFSET_WITH_SLOGAN : null;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${box.width} ${box.height}`}
            width={brandWidth(box, height)}
            height={height}
            focusable="false"
            {...(name ? { role: 'img', 'aria-label': name } : { 'aria-hidden': true })}
            className={cn('block shrink-0', className)}
            {...rest}
        >
            <path className={fills.mark} d={BRAND_PATHS.icon} />
            <g transform={offset ? `translate(${offset.x} ${offset.y})` : undefined}>
                <path className={fills.smart} d={BRAND_PATHS.smart} />
                <path className={fills.keur} d={BRAND_PATHS.keur} />
            </g>
            {slogan ? <path className={fills.slogan} d={BRAND_PATHS.slogan} /> : null}
        </svg>
    );
}
